import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import useCountUp from "@/hooks/useCountUp";
import {
  Megaphone, FileBarChart, MessageCircle, ChevronRight, AlertCircle,
  Loader2, CheckCircle2, XCircle, Clock, RefreshCw,
} from "lucide-react";

const cardCls =
  "bg-[#141414] border border-white/10 hover:border-white/20 rounded-lg shadow-sm p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30";

const sectionVariants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

function fmtDate(v) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "Never sent";
  return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function StatusDot({ isActive }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 ${
        isActive ? "bg-green-500/10 text-green-400" : "bg-white/10 text-slate-500"
      }`}
    >
      <span className="relative flex h-1.5 w-1.5">
        {isActive && (
          <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping" />
        )}
        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${isActive ? "bg-green-500" : "bg-slate-400"}`} />
      </span>
      {isActive ? "On" : "Off"}
    </span>
  );
}

function LastSentBadge({ status }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
        <Clock className="h-3.5 w-3.5" /> No data yet
      </span>
    );
  }
  const isGood = ["sent", "delivered", "read"].includes(status);
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${isGood ? "text-green-400" : "text-red-500"}`}>
      {isGood ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {status}
    </span>
  );
}

function Metric({ label, value, tone }) {
  const tones = {
    default: "text-white",
    green: "text-green-600",
    sky: "text-sky-400",
    red: "text-red-600",
  };
  const display = useCountUp(value);

  return (
    <div className="text-center">
      <p className={`font-heading text-2xl font-black ${tones[tone] || tones.default}`}>{display}</p>
      <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-400 mt-1">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const { activeBranch, activeBranchId } = useBranch();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [autoRows, setAutoRows] = useState([]);
  const [autoLoading, setAutoLoading] = useState(false);

  const load = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      const res = await api.get("/dashboard", { params: { branch_id: activeBranchId } });
      setData(res.data);
    } catch (e) {
      /* ignore */
    }
  }, [activeBranchId]);

  const loadAutomation = useCallback(async () => {
    if (!activeBranchId) return;
    setAutoLoading(true);
    try {
      const { data: rows } = await api.get("/automation-dashboard", { params: { branch_id: activeBranchId } });
      setAutoRows(rows);
    } finally {
      setAutoLoading(false);
    }
  }, [activeBranchId]);

  useEffect(() => {
    load();
    loadAutomation();
  }, [load, loadAutomation]);

  const lb = data?.last_broadcast;
  const u = data?.utility_today;

  const openConversation = (cid) => {
    if (cid) localStorage.setItem("open_conversation", cid);
    navigate("/wa-chat");
  };

  return (
    <motion.div data-testid="dashboard-page" variants={containerVariants} initial="hidden" animate="show">
      <motion.div variants={sectionVariants} className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-orange-600">
          {activeBranch ? activeBranch.name : "—"} Branch
        </p>
        <h1 className="font-heading text-4xl tracking-tight font-black text-white mt-1">Dashboard</h1>
      </motion.div>

      <motion.div variants={sectionVariants} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Last Broadcast */}
        <div data-testid="card-last-broadcast" className={cardCls}>
          <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-3">
            <h3 className="font-heading text-lg font-bold text-white">Last Broadcast</h3>
            <div className="h-8 w-8 rounded-md bg-orange-500/10 flex items-center justify-center">
              <Megaphone className="h-5 w-5 text-orange-600" />
            </div>
          </div>
          {lb ? (
            <div>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-white">{lb.name}</p>
                <span className="text-xs text-slate-400">{fmtDate(lb.created_at)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">Template: {lb.template_name}</p>
              <div className="grid grid-cols-4 gap-2 mt-4 border-t border-white/10 pt-3">
                <Metric label="Sent" value={lb.sent_count} />
                <Metric label="Delivered" value={lb.delivered_count} tone="green" />
                <Metric label="Read" value={lb.read_count} tone="sky" />
                <Metric label="Failed" value={lb.failed_count} tone="red" />
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-slate-400">No broadcasts sent yet</div>
          )}
        </div>

        {/* Utility Messages Report */}
        <div data-testid="card-utility-report" className={cardCls}>
          <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-3">
            <div>
              <h3 className="font-heading text-lg font-bold text-white">Utility Messages Report</h3>
              <p className="text-xs text-slate-400 mt-0.5">Today, per automation trigger</p>
            </div>
            <div className="h-8 w-8 rounded-md bg-orange-500/10 flex items-center justify-center shrink-0">
              <FileBarChart className="h-5 w-5 text-orange-600" />
            </div>
          </div>
          {u ? (
            <div>
              <div className="grid grid-cols-3 gap-2 pb-3 border-b border-white/10">
                <Metric label="Sent Today" value={u.totals.total} />
                <Metric label="Delivered" value={u.totals.delivered} tone="green" />
                <Metric label="Failed" value={u.totals.failed} tone="red" />
              </div>
              <div className="divide-y divide-white/10 mt-2">
                {u.rows.map((r) => (
                  <div key={r.trigger_key} data-testid={`utility-row-${r.trigger_key}`} className="py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white truncate">
                          {r.label}
                          {!r.is_active && (
                            <span className="ml-2 text-[10px] tracking-[0.1em] uppercase font-bold text-slate-400">Off</span>
                          )}
                        </p>
                        <p className="text-[11px] text-slate-400 truncate">{r.template_name}</p>
                      </div>
                      <div className="flex items-center gap-4 text-right shrink-0">
                        <div>
                          <p className="text-sm font-bold text-white">{r.total}</p>
                          <p className="text-[9px] uppercase tracking-wide text-slate-400">Sent</p>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-green-600">{r.delivered}</p>
                          <p className="text-[9px] uppercase tracking-wide text-slate-400">Delivered</p>
                        </div>
                        <div>
                          <p className={`text-sm font-bold ${r.failed ? "text-red-600" : "text-slate-600"}`}>{r.failed}</p>
                          <p className="text-[9px] uppercase tracking-wide text-slate-400">Failed</p>
                        </div>
                      </div>
                    </div>
                    {r.failed > 0 && r.failure_reason && (
                      <p className="flex items-start gap-1 text-xs text-red-600 mt-1.5">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-[1px]" />
                        <span className="truncate">{r.failure_reason}</span>
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-slate-400">No data</div>
          )}
        </div>
      </motion.div>

      {/* Recent Conversations */}
      <motion.div variants={sectionVariants} className={`mt-6 ${cardCls}`}>
        <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-3">
          <h3 className="font-heading text-lg font-bold text-white">Recent Conversations</h3>
          <MessageCircle className="h-5 w-5 text-orange-600" />
        </div>
        {data?.recent_conversations?.length ? (
          <div className="divide-y divide-white/10">
            {data.recent_conversations.map((c, i) => (
              <button
                key={i}
                data-testid={`recent-conversation-${i}`}
                onClick={() => openConversation(c.conversation_id)}
                className="w-full flex items-center justify-between gap-3 py-3 text-left hover:bg-white/5 transition-all duration-150 rounded-md px-2"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-white truncate">{c.contact_name}</p>
                  <p className="text-xs text-slate-500 truncate">{c.preview}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-600 shrink-0" />
              </button>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-slate-400">No recent inbound messages</div>
        )}
      </motion.div>

      {/* Automation Health */}
      <motion.div variants={sectionVariants} className={`mt-6 ${cardCls}`}>
        <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-3 gap-4 flex-wrap">
          <div>
            <h3 className="font-heading text-lg font-bold text-white">Automation Health</h3>
            <p className="text-xs text-slate-400 mt-0.5">Quick status check for all automated triggers</p>
          </div>
          <button
            onClick={loadAutomation}
            disabled={autoLoading}
            className="bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-3 py-1.5 text-sm transition-all duration-150 flex items-center gap-2 disabled:opacity-60"
          >
            {autoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>

        {autoLoading ? (
          <div className="py-16 text-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin inline" />
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr className="bg-white/5 border-b border-white/10">
                  {["Trigger", "Status", "Template", "Last Sent", "Sent Today"].map((h) => (
                    <th key={h} className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {autoRows.map((row) => (
                  <tr key={row.trigger_key} data-testid={`automation-dashboard-row-${row.trigger_key}`} className="border-b border-white/10 last:border-0">
                    <td className="px-4 py-3.5 font-semibold text-sm text-white">{row.label}</td>
                    <td className="px-4 py-3.5"><StatusDot isActive={row.is_active} /></td>
                    <td className="px-4 py-3.5 text-sm text-slate-200">{row.template_name}</td>
                    <td className="px-4 py-3.5">
                      <div className="text-sm text-slate-200">{formatDateTime(row.last_sent_at)}</div>
                      <LastSentBadge status={row.last_sent_status} />
                    </td>
                    <td className="px-4 py-3.5 text-sm font-bold text-white">{row.sent_today}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
